/**
 * Title resolution for graph objects.
 *
 * **Schema resolution** — identify *which* field is the title in a database:
 *   - `getTitleFieldId(db)`   → column ID
 *   - `getTitleFieldName(db)` → field name
 *   - `getTitleFieldIdFromProperties(props)` → column ID from a flat property
 *     list, for contexts where no `GraphDatabase` exists yet (type creation)
 *
 * **Value extraction** — read the title *value* from object data:
 *   - `useObjectTitle(controller, objectId)`       → reactive, releasable title binding (`null` if the object is missing; `"Untitled"` if it has no title)
 *   - `getObjectTitle(controller, objectId)`       → `ofObject` + `object.get` + `extractTitleFromGraphRow` (not `displayValue`, which is Title-column-only)
 *   - `displayTitle(title)`                       → UI string; blank/missing values become `"Untitled"`
 *   - `extractTitleFromGraphRow(db, record)`      → schema + row: title cell (`GraphResult` then plain string)
 *   - `extractTitleFromPlainRecord(record)`       → from a plain key/value record (no schema)
 *   - `extractTitleFromGraphResult(result)`       → from a typed `GraphResult`; self-resolves
 *     the schema from the `_routine.database` graph rows embed, when present
 */
import { computed } from "vue";

import type {
  Controller,
  GraphCustomType,
  GraphDatabase,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import {
  asRecordType,
  isRecordSchemaTextField,
} from "@/components/graph/predicates";
import {
  asGraphRecordResult,
  isPlainRecord,
  unwrapGraphObjectGetResult,
} from "@/components/graph/result";
import { withReleaseScope } from "@/ref";

const TITLE_NAME_CANDIDATES = [
  "Title",
  "title",
  "Name",
  "name",
  "FullName",
  "Full Name",
  "fullname",
];

// The name to display if we cannot find a title property.
const TITLE_UNDEFINED = "Untitled";

/** Display form of a title: blank or missing values become `"Untitled"`. */
export function displayTitle(title?: string | null): string {
  const trimmed = title?.trim();
  return trimmed ? trimmed : TITLE_UNDEFINED;
}

/**
 * What the title helpers need from a database schema. `GraphDatabase`
 * satisfies it, and so does the partial database graph rows embed in their
 * `_routine` meta record (where `type` arrives as a stringified schema and
 * the other `GraphDatabase` fields are irrelevant here).
 */
export type TitleSchemaSource = {
  meta?: { title_field?: string | null } | null;
  type?: GraphDatabase["type"] | string | null;
};

/**
 * The title field column ID when the schema *identifies* one: the configured
 * `meta.title_field`, else a field with a title-like name. Unlike
 * `getTitleFieldId` it never guesses — no first-text-field / first-field
 * fallback — mirroring the controller's own title rule (`tables.ml`). Used
 * where a wrong guess is worse than the plain-record heuristics that follow
 * (resolution through the schema rows embed in `_routine`).
 */
function identifiedTitleFieldId(db: TitleSchemaSource): string | undefined {
  const recordType = asRecordType(db.type);

  // Honor an explicitly configured title field even when the structured
  // schema isn't available. The `_routine.database` shipped with relation
  // cells doesn't carry the parsed `fields` map, so we trust the user's
  // configured column id directly in that case.
  const titleFieldId = db.meta?.title_field;
  if (!recordType) return titleFieldId ?? undefined;
  const fields = recordType.fields;

  if (titleFieldId && fields[titleFieldId]) {
    return titleFieldId;
  }

  const entries = Object.entries(fields);
  for (const candidate of TITLE_NAME_CANDIDATES) {
    const match = entries.find(([, f]) => f.name === candidate);
    if (match) return match[0];
  }

  return undefined;
}

/**
 * Resolve the title field **column ID** for a database, using:
 * 1. Explicit `meta.title_field` (column ID -> field name)
 * 2. Heuristic match against well-known title-like names
 * 3. First text-based (string) field
 * 4. First field of any type
 */
export function getTitleFieldId(
  db: TitleSchemaSource | null | undefined
): string | undefined {
  if (!db) return undefined;
  const identified = identifiedTitleFieldId(db);
  if (identified != null) return identified;

  const recordType = asRecordType(db.type);
  if (!recordType) return undefined;
  const entries = Object.entries(recordType.fields);

  const firstString = entries.find(([, field]) =>
    isRecordSchemaTextField(field)
  );
  if (firstString) return firstString[0];

  return entries[0]?.[0];
}

/** Resolve the title field **name** for a database. See `getTitleFieldId`. */
export function getTitleFieldName(
  db: TitleSchemaSource | null | undefined
): string | undefined {
  if (!db) return undefined;
  const recordType = asRecordType(db.type);
  if (!recordType) return undefined;
  const id = getTitleFieldId(db);
  return id ? recordType.fields[id]?.name : undefined;
}

/**
 * Resolve a field's display **name** from its column id, using the same schema
 * parsing as the title helpers (handles a stringified `db.type`). Returns
 * `undefined` when the database, column id, or field is missing.
 */
export function getFieldNameById(
  db: TitleSchemaSource | null | undefined,
  columnId: string | null | undefined
): string | undefined {
  if (!db || !columnId) return undefined;
  return asRecordType(db.type)?.fields[columnId]?.name;
}

/**
 * Resolve a field's column **id** from its display name. Returns `undefined`
 * when the database or field is missing. If `fieldName` is already a column
 * id present in the schema, it is returned as-is.
 */
export function getFieldIdByName(
  db: TitleSchemaSource | null | undefined,
  fieldName: string | null | undefined
): string | undefined {
  if (!db || !fieldName) return undefined;
  const fields = asRecordType(db.type)?.fields;
  if (!fields) return undefined;
  if (fields[fieldName]) return fieldName;
  for (const [id, field] of Object.entries(fields)) {
    if (field.name === fieldName) return id;
  }
  return undefined;
}

/**
 * Resolve a field's **type** (`GraphCustomType`) from its column id, using the
 * same schema parsing as the title helpers. Returns `undefined` when the
 * database, column id, or field is missing.
 */
export function getFieldTypeById(
  db: GraphDatabase | null | undefined,
  columnId: string | null | undefined
): GraphCustomType | undefined {
  if (!db || !columnId) return undefined;
  return db.type.fields[columnId]?.value;
}

/**
 * Resolve the title field **id** from a flat property list (no database schema).
 * Used when a database isn't available yet (e.g. type creation).
 */
export function getTitleFieldIdFromProperties(
  properties: ReadonlyArray<{ id: string; name: string }>
): string | undefined {
  for (const candidate of TITLE_NAME_CANDIDATES) {
    const match = properties.find((p) => p.name === candidate);
    if (match) return match.id;
  }
  return properties[0]?.id;
}

/**
 * `db` picks the title column; `record` is the row map. Last resort when the cell is already a plain string
 * (keys are usually column ids; drafts may use field names).
 */
function plainStringTitleFromGraphRow(
  db: TitleSchemaSource | null | undefined,
  record: Record<string, unknown>
): string | undefined {
  const fieldId = getTitleFieldId(db);
  if (fieldId != null) {
    const v = record[fieldId];
    if (typeof v === "string" && v.trim()) return v;
  }
  const name = getTitleFieldName(db);
  if (name != null) {
    const v = record[name];
    if (typeof v === "string" && v.trim()) return v;
  }
  return undefined;
}

/**
 * Title for one graph row: use schema (`db`) to find the title column, read values from `record`
 * (`GraphResult` first, then plain string). Falls back to candidate-name lookup when the schema
 * is missing or doesn't yield a value.
 */
export function extractTitleFromGraphRow(
  db: TitleSchemaSource | null | undefined,
  record: Record<string, unknown>
): string | undefined {
  return titleFromGraphRow(db, record, new WeakSet());
}

// `seen` guards against cyclic graph values (records can reference each
// other through relation cells): a record already being resolved higher up
// the chain yields no title instead of recursing forever.
function titleFromGraphRow(
  db: TitleSchemaSource | null | undefined,
  record: Record<string, unknown>,
  seen: WeakSet<object>
): string | undefined {
  const fieldId = getTitleFieldId(db);
  if (fieldId != null && record[fieldId] != null) {
    const t = titleFromGraphResult(record[fieldId], seen);
    if (t?.trim()) return t;
  }
  const name = getTitleFieldName(db);
  if (name != null && record[name] != null) {
    const t = titleFromGraphResult(record[name], seen);
    if (t?.trim()) return t;
  }
  const plainFromSchema = plainStringTitleFromGraphRow(db, record);
  if (plainFromSchema?.trim()) return plainFromSchema;
  return extractTitleFromPlainRecord(record, name);
}

/**
 * The (partial) database a graph row embeds in its `_routine` meta record —
 * the controller ships every row with its owning database there, including
 * the configured `meta.title_field` and a stringified `type` schema.
 * Undefined when the record isn't such a row or embeds no usable schema.
 */
function embeddedDatabase(
  record: Record<string, unknown>
): TitleSchemaSource | undefined {
  const routine = record["_routine"];
  if (!isPlainRecord(routine)) return undefined;
  const database = routine["database"];
  if (!isPlainRecord(database)) return undefined;
  const meta = database["meta"];
  const titleField = isPlainRecord(meta) ? meta["title_field"] : undefined;
  const type = database["type"];
  const schema: TitleSchemaSource = {
    meta: typeof titleField === "string" ? { title_field: titleField } : null,
    type: typeof type === "string" ? type : null,
  };
  return schema.meta || schema.type ? schema : undefined;
}

/**
 * Extract a human-readable title from a plain record (unknown-valued fields).
 *
 * 1. Explicit `titleFieldName` (e.g. from `getTitleFieldName(db)`)
 * 2. Heuristic match against well-known title-like names
 * 3. First non-empty string value (text-based property)
 * 4. `undefined` — callers that need a UI string should use {@link displayTitle}
 */
export function extractTitleFromPlainRecord(
  record: Record<string, unknown>,
  titleFieldName?: string
): string | undefined {
  if (titleFieldName) {
    const explicit = record[titleFieldName];
    if (typeof explicit === "string" && explicit.trim()) return explicit;
  }

  for (const candidate of TITLE_NAME_CANDIDATES) {
    const v = record[candidate];
    if (typeof v === "string" && v.trim()) return v;
  }

  for (const v of Object.values(record)) {
    if (typeof v === "string" && v.trim()) return v;
  }

  return undefined;
}

/**
 * Extract a human-readable title from a GraphResult.
 * Handles raw strings directly. Records that embed their owning database in
 * `_routine` (graph rows, relation cells) are resolved through that schema —
 * matching the controller's own title rule (`tables.ml`) — and fall back to
 * `extractTitleFromPlainRecord` heuristics, like schema-less records.
 * Returns `undefined` when no title can be resolved.
 */
export function extractTitleFromGraphResult(
  resultValue: unknown
): string | undefined {
  return titleFromGraphResult(resultValue, new WeakSet());
}

function titleFromGraphResult(
  resultValue: unknown,
  seen: WeakSet<object>
): string | undefined {
  if (!resultValue) return undefined;

  if (typeof resultValue === "string") return resultValue;
  if (!isPlainRecord(resultValue)) return undefined;
  if (seen.has(resultValue)) return undefined;
  seen.add(resultValue);

  // Resolve through the embedded schema only when it *identifies* the title
  // column; guessing (first text field) here could shadow the plain
  // heuristics below on well-known keys (e.g. built-in task/event rows carry
  // a literal "Title" key that their extension-table schema doesn't know).
  const db = embeddedDatabase(resultValue);
  const fieldId = db ? identifiedTitleFieldId(db) : undefined;
  if (db && fieldId != null) {
    // Rows are keyed by column id; drafts and built-in fields by name.
    for (const key of [fieldId, getFieldNameById(db, fieldId)]) {
      if (key == null || resultValue[key] == null) continue;
      const t = titleFromGraphResult(resultValue[key], seen);
      if (t?.trim()) return t;
    }
  }

  return extractTitleFromPlainRecord(resultValue);
}

/**
 * Reactive title for a graph object id using schema + row data
 * (`ofObject`, `object.get`). Unlike `object.displayValue`, this honors the
 * database's configured `meta.title_field`.
 */
export async function useObjectTitle(
  controller: Controller,
  objectId: string
): Promise<Readonly<ReleasableRef<string | null>>> {
  return withReleaseScope(async (own) => {
    const database = own(await controller.graph.database.ofObject(objectId));
    const objectResult = own(await controller.graph.object.get(objectId));
    return computed(() => {
      if (!database.value) return null;
      const data = unwrapGraphObjectGetResult(objectResult.value);
      if (data === null) return null;
      const record = asGraphRecordResult(data);
      if (!record) return null;
      return displayTitle(extractTitleFromGraphRow(database.value, record));
    });
  });
}

/** One-time snapshot of {@link useObjectTitle}. */
export async function getObjectTitle(
  controller: Controller,
  objectId: string
): Promise<string | null> {
  return (await useObjectTitle(controller, objectId)).release();
}
